import * as React from "react";
import {inject, observer} from "mobx-react";
import styled from "styled-components";
import type {IViewColumn, IViewSort} from "../../../../data/models/ViewUtils";
import {SortDirection} from "../../../../data/models/ViewUtils";
import type {IFieldPointer} from "../../../../data/models/field/Field";
import {XyiconFeature} from "../../../../generated/api/base";
import type {ManageColumnCallback} from "../../../widgets/table/TableHeaderDropDown";
import type {Pointer} from "../../../../utils/interaction/Pointer";
import type {View} from "../../../../data/models/View";
import type {App} from "../../../../App";
import type {AppState} from "../../../../data/state/AppState";
import type {IModel} from "../../../../data/models/Model";
import {Functions} from "../../../../utils/function/Functions";
import {ResizeDetector} from "../../../../utils/resize/ResizeDetector";
import {FocusLoss} from "../../../../utils/ui/focus/FocusLoss";
import {colorPalette} from "../../styles/colorPalette";
import {baseDistance, fontSize, radius} from "../../styles/styles";
import {LoaderStyled, LoaderV5} from "../../loader/LoaderV5";
import {ObjectUtils} from "../../../../utils/data/ObjectUtils";
import {CheckboxInputStyled} from "../../details/datatypes/CheckboxInputV5";
import {
	checkboxWidth,
	hasTableRowIconLeftPadding,
	minHeaderWidth,
	noTableRowIconLeftPadding,
	reportTableRowIconLeftPadding,
} from "../../../widgets/table/TableConstants";
import type {Report} from "../../../../data/models/Report";
import {TableFooterV5} from "./TableFooterV5";
import {QuickLinksV5} from "./QuickLinksV5";
import {OuterTableCellStyled, TableRowStyled, TableRowV5} from "./TableRowV5";
import type {SpotlightColorMode, TableFieldSpotlight} from "./TableRowV5";
import {TableHeadersCheckBoxColumnStyled, TableHeadersStyled, TableHeadersV5} from "./TableHeadersV5";
import {getPagedData} from "./PaginationUtils";
import {stabilizeTableHeaders} from "./stabilizeTableHeaders";
import {centerTableRow} from "./centerTableRow";

export interface ITableProps<T> {
	readonly loading?: boolean;
	readonly errorMessage?: string;
	readonly data: T[];
	readonly searchedData: T[];
	readonly selected?: T[];
	readonly focused?: T[];
	readonly checkboxColumn?: boolean;
	readonly moduleName?: string;
	readonly feature?: XyiconFeature;
	readonly tableRowIcon?: boolean;
	readonly searchString?: string;
	readonly isSearching?: boolean;
	readonly isCellContentsWrappingOn?: boolean;
	readonly sorts: IViewSort[];
	readonly view: View;
	readonly app?: App;
	readonly appState?: AppState;
	readonly isSimpleTable?: boolean;
	/** Preserve stable row props by preparing field values in each row. */
	readonly prepareFieldsInRow?: boolean;
	readonly isItemUnfocused?: boolean;
	readonly getItemColor?: (item: T) => string | null;
	readonly colorApplicationMode?: SpotlightColorMode;
	readonly getSpotlightFieldRefID?: (item: T, fieldRefID?: string) => string | null;
	readonly getColorApplicationMode?: (item: T) => SpotlightColorMode;
	readonly getSpotlightFieldColor?: (item: T, fieldRefID?: string) => string | null;

	readonly getHeaders: () => Partial<IViewColumn>[]; // TODO view is not nice here
	readonly onHeaderWidthChange: (index: number, width: number, header: Partial<IViewColumn>) => void;
	readonly getKey: (item: T, index: number) => string | number;
	readonly getFields: (item: T, index?: number) => (string | number)[];
	readonly getFieldsArray: (item: T, index?: number, headers?: readonly Partial<IViewColumn>[]) => any;
	readonly onFocus?: (items: T[], isGoToItemNeeded?: boolean) => void;
	readonly onSelect: (items: T[]) => void;
	readonly onClick?: (item: T) => void;
	readonly onDoubleClick?: (item: T) => void;
	readonly onDuplicateClick?: (item: T) => void;
	readonly onReorderColumn?: (index: number, newIndex: number) => void;
	readonly onToggleSort: (column: IFieldPointer) => void;
	readonly onManageColumns?: ManageColumnCallback;
	readonly onTableRowMouseMove?: (pointer: Pointer, row: TableRowV5<T>) => void;
	readonly onSortAsc?: (id: IFieldPointer) => void;
	readonly onSortDesc?: (id: IFieldPointer) => void;
	readonly onClearSort?: (id: IFieldPointer) => void;
	readonly runReport?: (reports: Report[]) => Promise<void>;
}

interface ITableState<T> {
	lastSelectedItem: T;
	currentPage: number;
	isQuickLinksOpen: boolean;
	quickLinkXPos: number;
	quickLinkYPos: number;
	quickLinkParentId: number;
}

const itemsPerPage: number = 100;

interface ItemScrollRequest {
	readonly id: string;
	readonly resolve: () => void;
	remainingFrames: number;
}

@inject("app")
@inject("appState")
@observer
export class TableV5<T> extends React.Component<ITableProps<T>, ITableState<T>> {
	public static readonly defaultProps = {
		checkboxColumn: true,
		sorts: [
			{
				column: "",
				direction: SortDirection.ASC,
			},
		],
		isSimpleTable: false,
		getKey: (item: IModel, index: number) => item?.id || index,
		getFilteredData: (items: IModel[]) => items,
		onSelect: Functions.emptyFunction,
	};

	private _header = React.createRef<HTMLTableSectionElement>();
	public _body = React.createRef<HTMLTableSectionElement>();
	public _table = React.createRef<HTMLTableElement>();
	private _resizeDetector: ResizeDetector;
	private _isMounted = false;
	private _maxTableWidth = 0;
	private _previewHeaderWidths = new Map<number, number>();
	private _rowHeaders: Partial<IViewColumn>[] = [];
	private _rowFieldSpotlights = new Map<string | number, readonly TableFieldSpotlight[]>();
	private _committedEditingSelection: Pick<ITableProps<T>, "selected" | "focused">;
	private _itemScrollFrame: number | null = null;
	private _pendingItemScroll: ItemScrollRequest | null = null;

	public container = React.createRef<HTMLDivElement>();

	private _hoveringModel: T;

	constructor(props: ITableProps<T>) {
		super(props);
		this._committedEditingSelection = {selected: props.selected?.slice(), focused: props.focused?.slice()};
		this.state = {
			lastSelectedItem: null,
			currentPage: 0,
			isQuickLinksOpen: false,
			quickLinkXPos: 0,
			quickLinkYPos: 0,
			quickLinkParentId: null,
		};
	}

	private commitEditingSelection() {
		const {selected, focused} = this.props;
		this._committedEditingSelection = {selected: selected?.slice(), focused: focused?.slice()};
	}

	private readonly getItemsToUpdate = (item: T): IModel[] => {
		const {selected, focused} = this._committedEditingSelection;
		const items = selected?.includes(item) ? selected : focused?.includes(item) ? focused : [item];
		return items as unknown as IModel[];
	};

	private updateQuickLinksStates = (x: number, y: number) => {
		this.setState({
			quickLinkXPos: x,
			quickLinkYPos: y,
			isQuickLinksOpen: true,
		});
	};

	private showQuickLinks = (e: React.MouseEvent, item: T, index: number) => {
		e.preventDefault();
		FocusLoss.listen(this.props.app.modalContainer, this.onTableBlur);
		this.setState({quickLinkParentId: index});

		if (this.state.isQuickLinksOpen) {
			this.hideQuickLinks();
		}

		let x = e.pageX;
		let y = e.pageY;

		this._hoveringModel = item;
		this.updateQuickLinksStates(x, y);
	};

	private scrollTableToShowFullCell = (e: React.MouseEvent) => {
		const table = this._table.current;

		if (table) {
			const tableBoundingBox = table.getBoundingClientRect();
			const divBoundingBox = e.currentTarget.getBoundingClientRect();
			const currentScrollValue = table.scrollLeft;
			const overFlow = divBoundingBox.right - tableBoundingBox.right;
			const underflow = divBoundingBox.left - tableBoundingBox.left;

			if (overFlow > 0) {
				table.scroll({left: currentScrollValue + overFlow});
			} else if (underflow < 0) {
				// add some px to be prettier
				table.scroll({left: currentScrollValue + underflow});
			}
			this.onTableScroll();
		}
	};

	private hideQuickLinks = () => {
		if (this.state.isQuickLinksOpen) {
			this.setState({
				isQuickLinksOpen: false,
			});
		}
	};

	private onTableScroll = () => {
		// This runs when the table is scrolled horizontally.
		// We need to set the width manually for header + body, to make sure the vertical scrollbar is positioned properly.
		// See:
		// https://stackoverflow.com/questions/48217171/horizontal-and-vertical-scroll-able-table-in-bootstrap

		const table = this._table.current;

		if (table) {
			const header = this._header.current;
			const body = this._body.current;

			// The sticky cells on the left get a shadow, when there's something scrolled behind them
			if (this.props.checkboxColumn) {
				if (table.scrollLeft > 0) {
					table.classList.add("scrolled");
				} else {
					table.classList.remove("scrolled");
				}
			}

			const containerWidth = this.container.current?.offsetWidth ?? 0;
			const contentWidth = Math.max(this._maxTableWidth, containerWidth);

			header.style.width = `${contentWidth}px`;
			body.style.width = `${contentWidth}px`;
		}
	};

	private onTableBlur = () => {
		this.hideQuickLinks();
	};

	public goToItem(item: any): Promise<void> {
		const pageIndex = this.getPageIndex(item);
		if (pageIndex < 0) {
			this.cancelItemScroll();
			return Promise.resolve();
		}
		return this.requestItemScroll(String(item.id), pageIndex);
	}

	public scrollToItemId(id: string) {
		void this.requestItemScroll(id);
	}

	private readonly cancelItemScroll = () => {
		if (this._itemScrollFrame !== null) {
			window.cancelAnimationFrame(this._itemScrollFrame);
			this._itemScrollFrame = null;
		}
		this._pendingItemScroll?.resolve();
		this._pendingItemScroll = null;
	};

	private requestItemScroll(id: string, pageIndex?: number): Promise<void> {
		this.cancelItemScroll();
		if (!this._isMounted) {
			return Promise.resolve();
		}
		return new Promise((resolve) => {
			const request: ItemScrollRequest = {id, resolve, remainingFrames: 3};
			this._pendingItemScroll = request;
			if (pageIndex !== undefined && this.state.currentPage !== pageIndex) {
				// A frame alone does not guarantee React has committed the new page.
				this.setState(
					(state) => (this._pendingItemScroll !== request || state.currentPage === pageIndex ? null : {currentPage: pageIndex}),
					() => this.scheduleItemScroll(request),
				);
			} else {
				this.scheduleItemScroll(request);
			}
		});
	}

	private scheduleItemScroll(request: ItemScrollRequest): void {
		if (this._pendingItemScroll !== request || !this._isMounted) {
			return;
		}
		this._itemScrollFrame = window.requestAnimationFrame(() => {
			this._itemScrollFrame = null;
			if (this._pendingItemScroll !== request || !this._isMounted) {
				return;
			}
			const table = this._table.current;
			const row = Array.from(this._body.current?.querySelectorAll<HTMLTableRowElement>("tr[data-key]") ?? []).find(
				(candidate) => candidate.dataset.key === request.id,
			);
			if (table && row) {
				centerTableRow(table, row, this._header.current);
			}
			// Native content-visibility can resolve wrapped row heights after scrolling.
			// Correct only across these next frames; manual input cancels the request.
			if (--request.remainingFrames > 0) {
				this.scheduleItemScroll(request);
			} else {
				this.cancelItemScroll();
			}
		});
	}

	private getPageIndex(item: any) {
		const {data} = this.props;
		const filteredData = this.props.searchedData || data;

		const index = filteredData.findIndex((candidate) => candidate === item || (item?.id != null && (candidate as any)?.id === item.id));

		if (index === -1) {
			return -1;
		}

		return Math.floor(index / itemsPerPage);
	}

	private onAutoResizeColumn = (index: number, header: Partial<IViewColumn>) => {
		const container = this.container.current;

		if (!container) {
			return;
		}

		// Add a dummy table, measure the column width, remove dummy table
		const table = document.createElement("table");

		table.className = "Table";
		table.style.position = "absolute";
		const tbody = document.createElement("tbody");

		const firstTR = document.createElement("tr");

		firstTR.innerHTML = this.props.getHeaders()[index].title;
		tbody.appendChild(firstTR);

		for (const data of this.props.data) {
			const tr = document.createElement("tr");

			const fields = this.props.getFields(data, 0);
			const field = fields[index] || "";

			tr.innerHTML = field.toString();
			tbody.appendChild(tr);
		}

		table.appendChild(tbody);
		container.appendChild(table);

		const padding = 20;
		const iconSize = 18;
		const maxWidth = 700;
		const width = Math.min(maxWidth, firstTR.clientWidth + 2 * padding + iconSize);

		container.removeChild(table);

		this.onHeaderWidthChange(index, width, header);
	};

	private isAllSelected(filteredData: T[]) {
		return filteredData.length === this.props.selected?.length;
	}

	private onAllClicked = () => {
		const filteredData = this.props.searchedData || this.props.data || [];

		if (this.isAllSelected(filteredData)) {
			this.props.onSelect([]);
		} else {
			this.props.onSelect(filteredData);
		}

		this.hideQuickLinks();
	};

	private onCheckboxRowClick = (event: React.MouseEvent, item: T) => {
		event.stopPropagation();

		this.hideQuickLinks();

		const index = this.props.selected.indexOf(item);

		if (index === -1) {
			if (event.shiftKey) {
				this.onShiftClick(item);
			} else {
				const selected = [...this.props.selected, item];

				this.props.onSelect(selected);
			}
		} else {
			const selected = this.props.selected.slice(0);

			selected.splice(index, 1);
			this.props.onSelect(selected);
		}

		if (!event.shiftKey && !this.props.selected.includes(item)) {
			this.setState({
				lastSelectedItem: item,
			});
		}
	};

	private onShiftClick = (item: T) => {
		const {data, selected, onSelect} = this.props;
		const lastIndex = data.indexOf(this.state.lastSelectedItem);
		const newIndex = data.indexOf(item);

		if (lastIndex > -1 && newIndex > -1) {
			if (selected?.includes(this.state.lastSelectedItem)) {
				const newSelection = [];
				const diff = newIndex - lastIndex;

				if (diff > 0) {
					for (let i = lastIndex; i <= newIndex; ++i) {
						newSelection.push(data[i]);
					}
				} else if (diff < 0) {
					for (let i = lastIndex; i >= newIndex; --i) {
						newSelection.push(data[i]);
					}
				}

				if (newSelection.length) {
					onSelect(newSelection);
				}
			} else {
				onSelect([item]);
			}
		}
	};

	private onFocusRow = (event: React.MouseEvent, item: T) => {
		const {focused = []} = this.props;
		const el = event.target as HTMLElement;
		const excludedCasses = ["quickLinksButton", "item", "label"];

		if (this.props.onClick) {
			this.props.onClick(item);
		}

		if (!excludedCasses.includes(el.className) || el.className.includes("disabled")) {
			const itemIndex = focused.indexOf(item);

			if (event.type === "click") {
				this.hideQuickLinks();
			}

			if (event.metaKey || event.ctrlKey) {
				const newFocusedItems = focused.slice(0);
				let isGoToItemNeeded = false;

				if (itemIndex === -1) {
					newFocusedItems.push(item);
					isGoToItemNeeded = true;
				} else {
					newFocusedItems.splice(itemIndex, 1);
				}

				this.props.onFocus?.(newFocusedItems, isGoToItemNeeded);
			} else if (event.shiftKey && this.state.lastSelectedItem) {
				this.onShiftClick(item);

				// Option B to remove selection (Option A is used now)
				// document.getSelection().removeAllRanges();
			} else {
				const newFocusedItems: T[] = [];
				let isGoToItemNeeded = false;

				if (itemIndex === -1) {
					newFocusedItems.push(item);
					isGoToItemNeeded = true;
				} else {
					newFocusedItems.splice(itemIndex, 1);
				}

				this.props.onFocus?.(newFocusedItems, isGoToItemNeeded);
			}

			if (!event.shiftKey && !focused.includes(item)) {
				this.setState({
					lastSelectedItem: item,
				});
			}
		}
	};

	public onPageChange = (page: number) => {
		this.cancelItemScroll();
		this.setState({currentPage: page});
		this._table.current?.scroll({top: 0});
	};

	private onDuplicateClick = (item: T) => {
		this.setState({isQuickLinksOpen: false});
		this.props.onDuplicateClick?.(item);
	};

	private calculateMaxTableWidth = () => {
		const {checkboxColumn, getHeaders, tableRowIcon, feature} = this.props;

		const resizerOverhang = 5;

		const effectiveCheckboxWidth = checkboxColumn ? checkboxWidth : 0;
		const tableRowIconWidth = feature === XyiconFeature.Report ? reportTableRowIconLeftPadding : hasTableRowIconLeftPadding;

		let sum = effectiveCheckboxWidth + (tableRowIcon ? tableRowIconWidth : noTableRowIconLeftPadding) + resizerOverhang;

		getHeaders().forEach((header, index) => (sum = sum + Math.max(this._previewHeaderWidths.get(index) ?? header.width ?? 0, minHeaderWidth)));

		return sum;
	};

	private applyColumnWidth = (index: number, width: number) => {
		const table = this._table.current;

		if (!table) {
			return;
		}

		const widthPx = `${width}px`;

		table.querySelectorAll<HTMLElement>(`[data-table-column-index="${index}"]`).forEach((element) => {
			element.style.width = widthPx;
			element.style.minWidth = widthPx;
		});
	};

	private onHeaderWidthPreview = (index: number, width: number, header: Partial<IViewColumn>) => {
		this._previewHeaderWidths.set(index, width);
		this.applyColumnWidth(index, width);
		this._maxTableWidth = this.calculateMaxTableWidth();
		this.onTableScroll();
	};

	private onHeaderWidthChange = (index: number, width: number, header: Partial<IViewColumn>) => {
		this._previewHeaderWidths.set(index, width);
		this.applyColumnWidth(index, width);
		this.props.onHeaderWidthChange(index, width, header);

		this._maxTableWidth = this.calculateMaxTableWidth();

		this.onTableScroll();

		requestAnimationFrame(() => {
			this._previewHeaderWidths.delete(index);
			this._maxTableWidth = this.calculateMaxTableWidth();
			this.onTableScroll();
		});
	};

	private onResize = () => {
		this._maxTableWidth = this.calculateMaxTableWidth();
		this.onTableScroll();
	};

	public override componentDidMount(): void {
		this.commitEditingSelection();
		const container = this.container.current;

		if (container) {
			this._resizeDetector = new ResizeDetector(container);
			this._resizeDetector.resize.add(this.onResize, this);
		}

		const table = this._table.current;

		if (table) {
			this._maxTableWidth = this.calculateMaxTableWidth();
			this.onTableScroll();
		}

		this._isMounted = true;
	}

	public override componentDidUpdate(prevProps: ITableProps<T>) {
		this.commitEditingSelection();
		const {searchString, view} = this.props;

		if (searchString !== prevProps.searchString || view !== prevProps.view || (!view && !ObjectUtils.compare(this.props.data, prevProps.data))) {
			this.setState((state) => (state.currentPage === 0 ? null : {currentPage: 0}));
		}

		if (view !== prevProps.view) {
			const table = this._table.current;
			const header = this._header.current;
			const body = this._body.current;

			if (table) {
				table.scrollLeft = 0;
			}

			if (header) {
				header.style.width = "";
			}
			if (body) {
				body.style.width = "";
			}

			this._maxTableWidth = this.calculateMaxTableWidth();
			requestAnimationFrame(() => this.onTableScroll());
		}

		if (this.props.data !== prevProps.data) {
			this._maxTableWidth = this.calculateMaxTableWidth();
			requestAnimationFrame(() => this.onTableScroll());
		}
	}

	public override componentWillUnmount() {
		this.cancelItemScroll();
		if (this._resizeDetector) {
			this._resizeDetector.resize.remove(this.onResize, this);
			this._resizeDetector.dispose();
		}

		this._isMounted = false;
	}

	public override render() {
		let {getFieldsArray} = this.props;
		const {
			checkboxColumn,
			loading,
			isSearching,
			errorMessage,
			moduleName,
			tableRowIcon,
			feature,
			view,
			sorts,
			isCellContentsWrappingOn,
			searchString,
			searchedData,
			focused,
			isItemUnfocused,
			isSimpleTable,
			getKey,
			getFields,
			onSortAsc,
			onSortDesc,
			onClearSort,
			onDoubleClick,
			onTableRowMouseMove,
			getHeaders,
			onToggleSort,
			onReorderColumn,
			onManageColumns,
			data,
			selected,
			getItemColor,
			getSpotlightFieldRefID,
			getColorApplicationMode,
			colorApplicationMode,
			getSpotlightFieldColor,
			runReport,
			appState,
			prepareFieldsInRow,
		} = this.props;
		const {isQuickLinksOpen, quickLinkXPos, quickLinkYPos, quickLinkParentId, currentPage} = this.state;

		getFieldsArray = getFieldsArray || getFields;

		const dataItems = data || [];
		const filteredData = searchedData || dataItems;
		const {pagedData, startIndex, lastIndex} = getPagedData(filteredData, currentPage, itemsPerPage);

		const selectedItems = selected || [];
		const allSelected = this.isAllSelected(filteredData);

		const headers = (this._rowHeaders = stabilizeTableHeaders(this._rowHeaders, getHeaders()));
		// ModuleView supplies fresh spotlight callbacks on every render. Resolve their
		// values here so unchanged rows can skip renders without hiding rule changes
		// stored in SpotlightRuleEvaluator's nonobservable cache.
		const previousFieldSpotlights = this._rowFieldSpotlights;
		this._rowFieldSpotlights = new Map();

		return (
			<TableContainerStyled
				className={`tableContainer module-${moduleName}`}
				ref={this.container}
			>
				{errorMessage ? (
					<div className="tableLoader">
						<div className="message">{errorMessage}</div>
					</div>
				) : (
					isSearching ||
					(loading && (
						<LoaderV5
							labelFirst={true}
							label={isSearching ? "Searching..." : "Fetching data..."}
						/>
					))
				)}
				<table
					ref={this._table}
					className="Table"
					onScroll={this.onTableScroll}
					onWheelCapture={this.cancelItemScroll}
					onTouchStartCapture={this.cancelItemScroll}
					onPointerDownCapture={this.cancelItemScroll}
					onKeyDownCapture={this.cancelItemScroll}
				>
					<TableHeadersV5
						headerRef={this._header}
						headers={headers}
						checkboxColumn={checkboxColumn}
						allSelected={allSelected}
						onAllClicked={this.onAllClicked}
						onToggleSort={onToggleSort}
						onAutoResizeColumn={this.onAutoResizeColumn}
						onReorderColumn={onReorderColumn}
						onHeaderWidthChange={this.onHeaderWidthChange}
						onHeaderWidthPreview={this.onHeaderWidthPreview}
						tableRowIcon={tableRowIcon}
						onManageColumns={onManageColumns}
						feature={feature}
						sorts={sorts}
						view={view}
						onSortAsc={onSortAsc}
						onSortDesc={onSortDesc}
						onClearSort={onClearSort}
					/>
					<tbody
						className="tableWrapper"
						ref={this._body}
					>
						{pagedData.map((item, index) => {
							index = startIndex + index;
							const fields = prepareFieldsInRow ? undefined : getFieldsArray(item, index, headers);
							const itemSelected = selectedItems.includes(item);
							const itemFocused = isItemUnfocused ? null : focused?.includes(item);
							const spotlightColor = getItemColor?.(item);
							const computedColorMode = getColorApplicationMode?.(item);
							const itemColorMode: SpotlightColorMode = computedColorMode ?? colorApplicationMode ?? "row";

							if (fields && fields.length !== headers.length) {
								console.warn("Header and fields length don't match!");
							}

							const key = getKey(item, index);
							let fieldSpotlights: readonly TableFieldSpotlight[];
							if (itemColorMode === "field" || itemColorMode === "both") {
								const next = headers.map(({field}) => {
									const fieldRefId = appState.actions.getFieldByRefId(field)?.refId;
									return fieldRefId && getSpotlightFieldRefID?.(item, fieldRefId) === fieldRefId
										? {color: getSpotlightFieldColor?.(item, fieldRefId) || spotlightColor || null}
										: null;
								});
								const previous = previousFieldSpotlights.get(key);
								fieldSpotlights =
									previous?.length === next.length && next.every((value, col) => !!value === !!previous[col] && value?.color === previous[col]?.color)
										? previous
										: next;
								this._rowFieldSpotlights.set(key, fieldSpotlights);
							}

							return (
								<TableRowV5
									key={key}
									item={item}
									id={key}
									fields={fields}
									getFieldsArray={prepareFieldsInRow ? getFieldsArray : undefined}
									headers={headers}
									index={index}
									numberOfItemsOnPage={pagedData.length}
									selected={itemSelected}
									focused={itemFocused}
									onFocus={this.onFocusRow}
									onDoubleClick={onDoubleClick}
									showQuickLinks={this.showQuickLinks}
									showCheckboxColumn={checkboxColumn}
									quickLinksActive={isQuickLinksOpen && quickLinkParentId == index}
									onCheckboxRowClick={this.onCheckboxRowClick}
									feature={feature}
									tableRowIcon={tableRowIcon}
									isCellContentsWrappingOn={isCellContentsWrappingOn}
									onMouseMove={onTableRowMouseMove}
									hasMultipleFocusedItems={(focused?.length ?? 0) > 1}
									scrollTableToShowFullCell={this.scrollTableToShowFullCell}
									isSimpleTable={isSimpleTable}
									getItemsToUpdate={this.getItemsToUpdate}
									spotlightColor={spotlightColor}
									colorApplicationMode={itemColorMode}
									fieldSpotlights={fieldSpotlights}
									runReport={runReport}
								/>
							);
						})}
						{
							// We need to pass portfolioID property, because we have to know which portfolio
							// links should appear in the QuickLinks component
							isQuickLinksOpen && appState.selectedFeature !== XyiconFeature.Portfolio && (
								<QuickLinksV5
									xPos={quickLinkXPos}
									yPos={quickLinkYPos}
									hoveringModel={this._hoveringModel}
									onDuplicateClick={this.onDuplicateClick}
									feature={feature}
								/>
							)
						}
					</tbody>
				</table>
				{filteredData.length > 0 && (
					<TableFooterV5
						itemsPerPage={itemsPerPage}
						totalCount={filteredData.length}
						startIndex={startIndex}
						lastIndex={lastIndex}
						currentPage={this.state.currentPage}
						onPageChange={this.onPageChange}
					/>
				)}
			</TableContainerStyled>
		);
	}
}

export const TableContainerStyled = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	position: relative;
	justify-content: space-between;
	overflow: hidden;
	background: ${colorPalette.white};
	flex-direction: column;
	font-size: ${fontSize.md};
	line-height: 16px;
	border-radius: ${radius.md};
	margin: 0 ${baseDistance.md} ${baseDistance.sm} ${baseDistance.md};
	contain: layout paint style;

	table {
		overflow-x: scroll;
		width: auto;
		min-width: 100%;
		display: flex;
		flex: 1;
		flex-direction: column;

		&.scrolled {
			${TableRowStyled} > td.sticky {
				box-shadow: 2px 0px 5px -1px rgba(0, 0, 0, 0.1);
			}

			${TableHeadersStyled} {
				th:first-child:has(${CheckboxInputStyled}) {
					padding-left: 12px;
				}
			}
		}
	}

	&.module-Settings {
		${OuterTableCellStyled} {
			align-items: center;
			padding: 0;
			left: 0;

			.simple {
				padding: 0 ${baseDistance.sm};
			}
		}

		${TableHeadersCheckBoxColumnStyled} {
			padding-right: 0px;
			padding-left: 2px;
		}

		.tableWrapper tr:not(.tableRowIcon) {
			padding-left: ${baseDistance.sm};
		}
	}

	${LoaderStyled} {
		& + table {
			display: none;
		}
	}
`;
